// ═══════════════════════════════════════════════════════════════════
// TMS GAMMA — Funções de Negócio
// Regras: cálculo de frete, validação de transportadora, SLA
// ═══════════════════════════════════════════════════════════════════
// A lista vem do registro unico em utils/transpLookup.js, alimentado pelo
// App com o que veio do Supabase. Antes havia um import estatico de
// constants/transportadoras.js, que colocava as 52 transportadoras reais no
// bundle inicial (#32) — e a constante nem existe mais.
//
// Usada em dois pontos deste arquivo, so para montar o nome legivel de uma
// mensagem de erro. Sem lista carregada, a mensagem sai com o codigo.
import { listarTransportadoras } from "./transpLookup";

// ─── Cálculo de frete ────────────────────────────────────────────────

/**
 * Calcula o frete total de uma tabela.
 * Retorna apenas o valor total (número).
 *
 * Fórmula:
 *   PCT → MAX(adv% × NF, mínimo)
 *   MIX → MAX(adv% × NF, R$/kg × peso, mínimo)
 *   KG  → MAX(R$/kg × peso, mínimo)
 *   + GRIS (% sobre NF, com mínimo em R$ se grisMin > 0)
 *   + Pedágio (por 100kg) + Despacho + TAS + TAD
 *   + ICMS (% sobre subtotal)
 */
export function calcFrete(tab, peso, nf, taxasMun) {
  return calcFreteDetalhado(tab, peso, nf, taxasMun).total;
}

/**
 * Mesmo cálculo, mas retorna o detalhamento completo por componente.
 * Útil para exibir o breakdown na Conferência e Auditoria.
 *
 * @returns {{
 *   pesoCalc: number,
 *   base: number,
 *   gris: number,
 *   pedagio: number,
 *   despacho: number,
 *   tas: number,
 *   tad: number,
 *   rdc: number,
 *   subtotal: number,
 *   icmsValor: number,
 *   total: number,
 * }}
 */
export function calcFreteDetalhado(tab, peso, nf, taxasMun) {
  // taxasMun: entrada do campo municipios.taxas para a transportadora específica
  // Ex: { codTransp:"000016", tda:99.79, tas:0, tde:0, tad:0 }
  // Quando presente e > 0, os valores do município prevalecem sobre a tabela.
  if (!tab) return { pesoCalc:0, base:0, freteExcedente:0, gris:0, advExtra:0, pedagio:0, despacho:0, tas:0, tad:0, tda:0, rdc:0, subtotal:0, icmsValor:0, total:0 };

  // Peso com fracionamento
  let pc = Math.max(+peso || 0, 0);
  if (tab.frac === "Arredonda 1kg")  pc = Math.ceil(pc);
  if (tab.frac === "Arredonda 100g") pc = Math.ceil(pc * 10) / 10;

  const nfN = +nf || 0;

  // Frete base por tipo
  let base = 0;
  let freteExcedente = 0;     // só usado em FAIXAS — valor além da maior faixa, agora separado da base
  let pesoMaxFaixaTab = null; // hoisted pra fora do bloco FAIXAS — reaproveitado no despacho, abaixo
  if (tab.tipo === "PCT") base = Math.max(nfN * ((tab.adv || 0) / 100), tab.minimo || 0);
  if (tab.tipo === "MIX") base = Math.max(nfN * ((tab.adv || 0) / 100), (tab.pgv || 0) * pc, tab.minimo || 0);
  // KG com degrau (#471): acima de `pesoAcima` o peso TODO vale `rkgAcima`
  // (Risso: 0,74 até 6 t, 0,64 acima). Sem degrau, vale `rkg`.
  const rkgKG = tab.pesoAcima > 0 && tab.rkgAcima > 0 && pc > tab.pesoAcima ? tab.rkgAcima : tab.rkg;
  if (tab.tipo === "KG")  base = Math.max((rkgKG || 0) * pc, tab.minimo || 0);

  if (tab.tipo === "FAIXAS") {
      // Tabela por faixas de peso: cada faixa tem um valor fixo.
      // Acima da MAIOR faixa cadastrada: valor dessa faixa (base) + excedente
      // por kg (freteExcedente) — os dois ficam SEPARADOS agora, porque
      // algumas transportadoras (ex: Alfa) declaram o excedente como um
      // componente próprio no CT-e ("FRETE PESO"), distinto do frete base
      // ("SECCAT"). Antes os dois vinham somados dentro de "base", sem
      // como distinguir na composição do CT-e.
    // Faixa SEM VALOR não existe (#132). O formulário de simulação nasce com
    // as dez faixas em `valor: 0` (FAIXAS_DEFAULT) e quem simula preenche só
    // algumas -- as vazias ficavam na lista valendo zero, e isso produzia
    // dois fretes fantasma:
    //
    //   1. peso caindo numa faixa vazia  -> frete R$ 0,00;
    //   2. peso acima da última faixa PREENCHIDA -> base zerada, e o
    //      excedente contado a partir da última faixa da lista (vazia).
    //
    // Medido num comparativo real de 1.110 notas: 373 delas (34%) com frete
    // errado, R$ 111.590,64 a menos. A NF 14195 (340 kg) saiu a R$ 42,40 --
    // (340-300) x 1,06 -- quando o certo era 214,99 + (340-200) x 1,06.
    // Faixa vale se tiver PESO LIMITE e VALOR. Sem valor era o defeito da
    // #132; sem peso passou a ser possível quando a simulação deixou de
    // impor degraus fixos e quem simula passou a digitar o `ate` (#134) --
    // uma linha recém-adicionada nasce vazia, e meia faixa não é faixa.
    const faixas = (tab.faixas || [])
      .filter(f => +f.ate > 0 && +f.valor > 0)
      .slice().sort((a,b) => +a.ate - +b.ate);
    if (faixas.length > 0) {
      // Tudo com `+`: a faixa pode chegar como TEXTO (vem de um input, ou do
      // banco). Sem isso `base` vira string, a soma do subtotal concatena e
      // o cálculo morre em `.toFixed is not a function` -- o mesmo defeito
      // que `tas`/`tad` tinham (#132).
      const ultimaFaixa   = faixas[faixas.length - 1];
      const pesoMaxFaixa  = +ultimaFaixa.ate; // dinâmico — vem da própria tabela
      pesoMaxFaixaTab = pesoMaxFaixa;
      if (pc <= pesoMaxFaixa) {
        // Encontra a menor faixa que comporta o peso
        const faixa = faixas.find(f => pc <= +f.ate) || ultimaFaixa;
        base = +faixa.valor || 0;
      } else {
        const excedente = +tab.excedente || 0;
        base = +ultimaFaixa.valor || 0;                    // só a faixa
        freteExcedente = (pc - pesoMaxFaixa) * excedente;  // excedente isolado
      }
    }
  }

  // ── GRIS ─────────────────────────────────────────────────────────
  // Regra: se grisMin > 0, aplica o máximo entre o percentual e o mínimo.
  // Válido para TODOS os tipos (PCT, MIX, KG, FAIXAS).
  // Se grisMin = 0, usa só o percentual (comportamento anterior).
  const grisValor = nfN * ((tab.gris || 0) / 100);
  const grisMin   = tab.grisMin || 0;
  const gris      = grisMin > 0 ? Math.max(grisValor, grisMin) : grisValor;

  // ADV extra — só para FAIXAS (onde AdV é componente separado do frete base)
  const advValor = nfN * ((tab.adv || 0) / 100);
  const advMin   = tab.advMin || 0;
  const advExtra = tab.tipo === "FAIXAS" ? Math.max(advValor, advMin) : 0;

  // Pedágio: R$/100kg × frações de 100kg; aplica mínimo se pedMin configurado
  let pedagio = (tab.ped || 0) * Math.ceil(pc / 100);
  if (tab.pedMin && tab.pedMin > 0 && pedagio > 0 && pedagio < tab.pedMin) {
    pedagio = tab.pedMin;
  }
  // Mesmo bug do limite "300kg fixo" que existia em `base` — corrigido do
  // mesmo jeito: usa o limite real da maior faixa cadastrada na tabela
  // (pesoMaxFaixaTab), não um número fixo que só valia pras tabelas cuja
  // faixa mais alta coincidia com 300kg.
  const despacho = tab.tipo === "FAIXAS"
    ? (pesoMaxFaixaTab != null && pc > pesoMaxFaixaTab ? (tab.desp || 0) : 0)
    : (tab.desp || 0);
  // ── TAS, TAD e TDA ───────────────────────────────────────────────
  // ⚠ TAD e TDA são taxas DIFERENTES e já foram tratadas como uma só:
  //
  //   TAD — Taxa de Administração/Entrega. Vem da TABELA de frete; o
  //         município pode ter um valor próprio que prevalece.
  //   TDA — Taxa de Difícil Acesso. Existe SÓ no município: é uma
  //         característica do destino, não do contrato. A tabela nem tem
  //         campo para ela.
  //
  // A linha antiga era `tad = taxasMun.tda ?? tab.tad`: o difícil acesso do
  // município entrava no lugar do TAD e ainda APAGAVA o valor da tabela.
  // Num CT-e real, TAD 6,10 (tabela) e difícil acesso 99,79 (município) eram
  // cobrados lado a lado, e o cálculo mostrava 99,79 como TAD e nada de TDA —
  // duas divergências inventadas de uma vez, e um total 6,10 menor.
  // `??` só ignora nulo e indefinido -- um campo de TEXTO VAZIO passava
  // direto (#132). Aí `subtotal` virava string por concatenação e o cálculo
  // morria em `.toFixed is not a function`. Número é número: `+valor || 0`.
  const num = v => +v || 0;
  const tas = num((taxasMun?.tas > 0 ? taxasMun.tas : null) ?? tab.tas);
  const tad = num((taxasMun?.tad > 0 ? taxasMun.tad : null) ?? tab.tad);
  const tda = taxasMun?.tda > 0 ? num(taxasMun.tda) : 0;

  // ── RDC ──────────────────────────────────────────────────────────
  // Três modalidades:
  //   fixo      → valor fixo em R$
  //   percNota  → % sobre o valor da nota fiscal (padrão/legado)
  //   percFrete → % sobre o frete base já calculado
  const rdcTipo = tab.rdcTipo || "percNota";
  const rdc     = rdcTipo === "fixo"      ? (tab.rdc || 0)
                : rdcTipo === "percFrete" ? base * ((tab.rdc || 0) / 100)
                :                           nfN  * ((tab.rdc || 0) / 100);

  const subtotal  = base + freteExcedente + gris + advExtra + pedagio + despacho + tas + tad + tda + rdc;

  // ICMS incide sobre o subtotal (por dentro)
  const icmsValor = (subtotal / ((100 - (tab.icms || 0)) / 100)) * ((tab.icms || 0) / 100);

  const total = +(subtotal + icmsValor).toFixed(2);

  return { pesoCalc: pc, base, freteExcedente, gris, advExtra, pedagio, despacho, tas, rdc, tad, tda, subtotal, icmsValor, total };
}

// ─── Taxas municipais e de cliente ──────────────────────────────────
// Centralizadas aqui porque eram reimplementadas de forma idêntica em
// Conferencia, Comparativo, ComparativoMunicipio e Cotacao. Qualquer
// correção de regra de taxa agora se propaga para todos os módulos
// de uma vez, sem precisar editar tela por tela.

/**
 * Retorna o registro de taxas (tda/tde/tad/tas) de um município
 * para uma transportadora específica, ou null se não houver.
 */
export function getTaxasMun(codTransp, municipio) {
  if (!municipio?.taxas) return null;
  const codN = String(codTransp || "").replace(/^0+/, "");
  return municipio.taxas.find(tx => {
    const txN = String(tx.codTransp || "").replace(/^0+/, "");
    return txN === codN || tx.codTransp === codTransp;
  }) || null;
}

/**
 * Retorna o valor de TDE configurado para um cliente específico
 * com uma transportadora específica (cadastro de Clientes).
 */
export function getTdeCliente(codTransp, cliente) {
  // A importação grava o código como veio na planilha ("16") e a tabela usa
  // "000016": a busca exata nunca casava (#85). Compara sem zeros à esquerda.
  const codN = String(codTransp || "").replace(/^0+/, "");
  if (!cliente?.tde || !codN) return 0;
  const chave = Object.keys(cliente.tde).find(k => k.replace(/^0+/, "") === codN);
  return +cliente.tde[chave] || 0;
}

/**
 * Acha o cliente pelo que foi digitado na Cotação/Conferência: CNPJ (14
 * dígitos, com ou sem máscara) ou código de 6 dígitos + loja (#320).
 * O CNPJ já identifica a loja, então a loja só vale para o código.
 */
export function acharCliente(clientes, termo, loja) {
  const dig = String(termo || "").replace(/\D/g, "");
  if (!clientes?.length) return null;
  if (dig.length === 14) return clientes.find(c => String(c.cnpj || "").replace(/\D/g, "") === dig) || null;
  if (dig.length === 6)  return clientes.find(c => c.cod === dig && c.loja === loja) || null;
  return null;
}

/**
 * Retorna o prazo de um município para uma transportadora, olhando
 * qual dos três papéis (Padrão/MEC/Rural) ela ocupa no cadastro.
 */
/**
 * Prazo da TABELA DE FRETE para a rota (transportadora → município destino).
 *
 * ⚠ ESTA É A FONTE OFICIAL DO PRAZO (regra confirmada em 23/07/2026).
 * O cadastro de município é apenas fallback. Estava definida dentro de
 * NotasFiscais, então a Auditoria de CT-e usava o prazo do município e
 * media atraso de transportadora com uma régua diferente da do OTIF — o que
 * pesa, porque a auditoria embasa cobrança. Mora aqui para ter uma
 * implementação só.
 */
// Prazo da tabela VIGENTE em `dataISO` (#157). Sem a data, o prazo de uma
// entrega antiga era o da tabela atual: trocar 3d → 1d na tabela reescrevia o
// OTIF de meses fechados. Para mudar prazo daqui em diante, encerre a tabela
// (vigFim) e cadastre outra — editar a rota da mesma tabela ainda reescreve.
// Sem dataISO vale hoje (contrato atual).
export function getPrazoTabela(codTransp, munDestino, tabelas, dataISO) {
  if (!codTransp || !munDestino || !tabelas?.length) return null;
  const dest = String(munDestino).toUpperCase().trim();
  // Sem zeros à esquerda: a base mistura "92" e "000092".
  const codN = String(codTransp).replace(/^0+/, "");
  const comRota = [];
  for (const tab of tabelas) {
    const tN = String(tab.codTransp || "").replace(/^0+/, "");
    if (!(tN === codN || tab.codTransp === codTransp) || !tab.rotas?.length) continue;
    const rota = tab.rotas.find(r => r.dest?.toUpperCase().trim() === dest)
              || tab.rotas.find(r => dest.includes(r.dest?.toUpperCase().trim() || "~~"))
              || tab.rotas.find(r => r.dest?.toUpperCase().trim() === "TODOS");
    if (rota?.prazo) comRota.push({ tab, prazo: +rota.prazo });
  }
  if (!comRota.length) return null;

  const d   = (dataISO || new Date().toISOString()).slice(0, 10);
  const ini = t => (t.vigIni || "0000-01-01").slice(0, 10);
  const fim = t => (t.vigFim || "9999-12-31").slice(0, 10);
  const vigente = comRota.find(c => ini(c.tab) <= d && d <= fim(c.tab));
  if (vigente) return vigente.prazo;
  // Nenhuma cobre a data (tabela vencida e não renovada é comum): a mais
  // recente que já tinha começado; senão qualquer uma — como era antes.
  const anterior = comRota.filter(c => ini(c.tab) <= d)
    .sort((a, b) => ini(b.tab).localeCompare(ini(a.tab)))[0];
  return (anterior || comRota[0]).prazo;
}

export function getPrazoMun(codTransp, municipio) {
  if (!municipio || !codTransp) return null;
  if (municipio.transpPadrao === codTransp) return municipio.prazoPadrao;
  if (municipio.transpMec    === codTransp) return municipio.prazoMec;
  if (municipio.transpRural  === codTransp) return municipio.prazoRural;
  return null;
}

/**
 * Busca a rota (origem→destino) de uma tabela que cobre o par informado.
 * Retorna a rota encontrada ou null.
 *
 * Prioridade de matching, do mais confiável para o mais frágil:
 *   1. IBGE exato (orig+dest) — quando a rota foi vinculada ao cadastro
 *   2. IBGE só do destino
 *   3. Nome exato (orig+dest) — fallback para rotas digitadas como texto livre
 *   4. Nome só do destino
 *   5. Rota "TODOS" (cobre qualquer destino)
 *
 * munOrigem/munDest são objetos do cadastro de municípios (têm .mun e,
 * quando disponível, .codIBGE). Rotas que já foram vinculadas ao IBGE
 * (via editor ou pelo botão "Vincular IBGE") têm rota.destIBGE/origIBGE.
 */
export function getRotaMatch(tabela, munOrigem, munDest) {
  if (!tabela?.rotas?.length) return null;
  const orig = munOrigem?.mun?.toUpperCase() || "";
  const dest = munDest?.mun?.toUpperCase()   || "";
  const origIBGE = munOrigem?.codIBGE || "";
  const destIBGE = munDest?.codIBGE   || "";
  if (!dest && !destIBGE) return null;

  // 1. IBGE exato (orig+dest) — só faz sentido quando a rota tem origIBGE
  if (origIBGE && destIBGE) {
    const exataIBGE = tabela.rotas.find(r => r.origIBGE && r.destIBGE &&
      r.origIBGE === origIBGE && r.destIBGE === destIBGE);
    if (exataIBGE) return exataIBGE;
  }

  // 2. IBGE só do destino
  if (destIBGE) {
    const porDestIBGE = tabela.rotas.find(r => r.destIBGE && r.destIBGE === destIBGE);
    if (porDestIBGE) return porDestIBGE;
  }

  // 3. Nome exato (orig+dest) — fallback para rotas ainda não vinculadas
  if (orig && dest) {
    const exata = tabela.rotas.find(r =>
      r.orig?.toUpperCase() === orig &&
      (r.dest?.toUpperCase() === dest || r.dest?.toUpperCase() === "TODOS")
    );
    if (exata) return exata;
  }

  // 4. Nome só do destino
  if (dest) {
    const porDest = tabela.rotas.find(r =>
      r.dest?.toUpperCase() === dest || r.dest?.toUpperCase() === "TODOS"
    );
    if (porDest) return porDest;
  }

  // 5. Rota "TODOS" sem destino específico informado (caso raro)
  return tabela.rotas.find(r => r.dest?.toUpperCase() === "TODOS") || null;
}

/**
 * A tabela serve para a conferência origem→destino?
 * - transpsDaOrigem: Set de códigos (sem zeros à esquerda) que atendem a
 *   filial da origem; null = sem restrição.
 * - munDest: com destino, exige rota que o cubra (getRotaMatch). Tabela sem
 *   nenhuma rota passa — limite registrado na #230.
 */
export function tabelaAtende(tabela, transpsDaOrigem, munDest) {
  if (transpsDaOrigem && !transpsDaOrigem.has(String(tabela.codTransp || "").replace(/^0+/, ""))) return false;
  if (munDest && tabela.rotas?.length && !getRotaMatch(tabela, null, munDest)) return false;
  return true;
}

/**
 * Resolve o código IBGE de um nome de município (+ UF opcional) contra
 * o cadastro já carregado. Usado para vincular rotas de tabelas_frete
 * (hoje texto livre) ao cadastro oficial, sem precisar de import externo.
 * Tenta nome+UF exato primeiro, depois só nome, depois nome contido
 * (para variações leves de digitação tipo "S PAULO" vs "SÃO PAULO").
 */
export function resolveRotaIBGE(nome, uf, municipios) {
  if (!nome || !municipios?.length) return null;
  const nomeU = String(nome).toUpperCase().trim();
  const ufU   = String(uf || "").toUpperCase().trim();

  if (ufU) {
    const exato = municipios.find(m =>
      m.mun?.toUpperCase().trim() === nomeU && m.uf?.toUpperCase().trim() === ufU);
    if (exato?.codIBGE) return exato.codIBGE;
  }

  const porNome = municipios.find(m => m.mun?.toUpperCase().trim() === nomeU);
  if (porNome?.codIBGE) return porNome.codIBGE;

  return null; // não força match parcial aqui — risco de vincular errado
}

/**
 * Frete total de uma tabela incluindo taxas do município de destino
 * e TDE específico do cliente. É o cálculo "completo" usado em telas
 * de simulação/conferência — diferente de calcFrete, que só resolve
 * a fórmula da tabela sem olhar município/cliente.
 */
export function calcFreteTotal(tabela, pesoN, nfN, municipioDest, cliente) {
  if (!tabela) return 0;
  const base = calcFrete(tabela, pesoN, nfN);
  if (base <= 0) return 0;
  const taxasMun   = getTaxasMun(tabela.codTransp, municipioDest);
  const tdeCliente = getTdeCliente(tabela.codTransp, cliente);
  return base
    + (taxasMun?.tde || 0)
    + (taxasMun?.tda || 0)
    + (taxasMun?.tad || 0)
    + (taxasMun?.tas || 0)
    + tdeCliente;
}


export const RDC_TIPOS = [
  { value: "fixo",      label: "Valor Fixo (R$)" },
  { value: "percNota",  label: "% sobre o valor da nota" },
  { value: "percFrete", label: "% sobre o frete base" },
];

export function rdcUnidade(tab) {
  return (tab?.rdcTipo || "percNota") === "fixo" ? "R$" : "%";
}

export function rdcDescricao(tab) {
  const tipo = tab?.rdcTipo || "percNota";
  if (tipo === "fixo")      return "RDC (Valor Fixo)";
  if (tipo === "percFrete") return "RDC (% sobre frete)";
  return "RDC (% sobre nota)";
}

export function fmtRdcValor(tab) {
  const tipo = tab?.rdcTipo || "percNota";
  const v = tab?.rdc || 0;
  return tipo === "fixo" ? "R$ " + v.toFixed(2) : v + "%";
}

// ─── Dias úteis ──────────────────────────────────────────────────────

// Feriados nacionais fixos — piso mínimo, usado quando o cadastro dinâmico
// (tabela `feriados`) ainda não foi carregado. O cadastro complementa esta
// lista; não a substitui.
// Map e não Set: o nome é o que a tela mostra quando o prazo escorrega.
// "Prazo estendido em 1 dia" sem dizer por quê parece erro de cálculo.
const FERIADOS = new Map([
  ["2026-01-01","Confraternização Universal"],
  ["2026-04-21","Tiradentes"],
  ["2026-05-01","Dia do Trabalho"],
  ["2026-09-07","Independência"],
  ["2026-10-12","Nossa Senhora Aparecida"],
  ["2026-11-02","Finados"],
  ["2026-11-15","Proclamação da República"],
  ["2026-12-25","Natal"],
]);

// Feriados vindos do banco, instalados por instalarFeriados(). Fica em módulo
// para que as dezenas de chamadas de addDiasUteis espalhadas pelo sistema não
// precisem receber a lista por parâmetro — o que exigiria alterar cada uma e,
// na primeira que fosse esquecida, o prazo divergiria de novo.
let _feriadoExtra = null;   // (isoDate, ctx) => boolean

export function instalarFeriados(fn) { _feriadoExtra = fn || null; }

// Comparação de município tolerante a acento e caixa: a nota traz
// "SAO JOSE DOS PINHAIS" e o cadastro pode ter "São José dos Pinhais".
function chaveMun(s) {
  return String(s || "").normalize("NFD").replace(/[̀-ͯ]/g, "")
    .trim().toUpperCase();
}

/**
 * Monta o teste de feriado a partir das linhas do banco.
 *
 * Indexa por DATA primeiro: o cálculo de prazo chama isto uma vez por dia
 * percorrido, para dezenas de milhares de notas. Varrer a lista inteira a
 * cada chamada custaria segundos; com o índice, a esmagadora maioria das
 * datas nem tem entrada e sai no primeiro teste.
 */
export function criarFeriadoLookup(feriados) {
  const porData = new Map();
  (feriados || []).forEach(f => {
    const d = f.data_iso || f.dataISO;
    if (!d) return;
    if (!porData.has(d)) porData.set(d, []);
    porData.get(d).push({
      nome:        f.nome || f.descricao || "Feriado",
      abrangencia: f.abrangencia || "nacional",
      municipio:   chaveMun(f.municipio),
      uf:          String(f.uf || "").trim().toUpperCase(),
      filial:      String(f.filial || "").trim().replace(/^0+/, ""),
    });
  });

  // ⚠ Devolve o FERIADO que casou (objeto) ou null — não mais um booleano.
  // Objeto é truthy, então isUtil e todo consumidor de teste-de-verdade
  // continuam funcionando; quem precisa do nome agora consegue obtê-lo.
  return (iso, ctx) => {
    const lista = porData.get(iso);
    if (!lista) return null;
    const mun = chaveMun(ctx?.municipio);
    const uf  = String(ctx?.uf     || "").trim().toUpperCase();
    const fil = String(ctx?.filial || "").trim().replace(/^0+/, "");
    return lista.find(f => {
      if (f.abrangencia === "nacional") return true;
      // Estadual: vale para qualquer município da UF (9 de julho em SP para o
      // estado inteiro). Sem a UF no contexto não há como afirmar que vale,
      // então não aplica — melhor um dia útil a mais que um prazo encurtado
      // para uma nota de outro estado.
      if (f.abrangencia === "estadual") return !!uf && f.uf === uf;
      if (f.abrangencia === "filial")   return !!fil && f.filial === fil;
      // municipal: casa pelo nome; a UF só restringe quando ambos a informam,
      // senão um cadastro sem UF deixaria de valer para a nota que tem UF.
      if (!mun || f.municipio !== mun) return false;
      return !f.uf || !uf || f.uf === uf;
    }) || null;
  };
}

function isoLocal(d) {
  // toISOString() converte para UTC: no fuso -03:00 uma data às 12:00 local
  // vira o MESMO dia, mas a margem é pequena. Montar a partir dos getters
  // locais elimina qualquer risco de virar o dia.
  return d.getFullYear() + "-" +
    String(d.getMonth() + 1).padStart(2, "0") + "-" +
    String(d.getDate()).padStart(2, "0");
}

function isUtil(d, ctx) {
  const dia = d.getDay();
  if (dia === 0 || dia === 6) return false;
  const iso = isoLocal(d);
  if (FERIADOS.has(iso)) return false;
  if (_feriadoExtra && _feriadoExtra(iso, ctx)) return false;
  return true;
}

// Qual feriado cai neste dia, para este contexto — ou null. Fim de semana
// não conta: sábado e domingo não surpreendem ninguém, e reportá-los junto
// afogaria o aviso que importa.
function feriadoDe(d, ctx) {
  const dia = d.getDay();
  if (dia === 0 || dia === 6) return null;
  const iso = isoLocal(d);
  const fixo = FERIADOS.get(iso);
  if (fixo) return { iso, nome: fixo, abrangencia: "nacional" };
  const extra = _feriadoExtra && _feriadoExtra(iso, ctx);
  return extra ? { iso, nome: extra.nome, abrangencia: extra.abrangencia } : null;
}

const ROTULO_ABRANGENCIA = {
  nacional: "nacional", estadual: "estadual",
  municipal: "municipal", filial: "da filial",
};

/**
 * Igual a addDiasUteis, mas informa QUAIS feriados empurraram a data.
 *
 * Existe porque uma previsão que escorrega dois dias sem explicação parece
 * erro de cadastro — e o usuário refaz a conta à mão para conferir. Dizer
 * "estendido em 2 dias: Natal (nacional), aniversário da cidade (municipal)"
 * encerra a dúvida na hora e evita o chamado.
 *
 * Devolve { data, feriados, diasExtras }. `diasExtras` é quantos dias
 * CORRIDOS a mais a previsão levou por causa dos feriados — que é o número
 * que o usuário percebe, não a quantidade de feriados.
 */
export function addDiasUteisDetalhado(dataISO, dias, ctx) {
  const vazio = { data: "—", feriados: [], diasExtras: 0 };
  if (!dataISO || !dias || isNaN(+dias)) return vazio;
  try {
    const d = new Date(dataISO + "T12:00:00");
    if (isNaN(d.getTime())) return vazio;
    const feriados = [];
    let c = 0, guarda = 0;
    while (c < dias && guarda++ < 400) {
      d.setDate(d.getDate() + 1);
      const f = feriadoDe(d, ctx);
      if (f) feriados.push({ ...f, rotulo: ROTULO_ABRANGENCIA[f.abrangencia] || f.abrangencia });
      if (isUtil(d, ctx)) c++;
    }
    return { data: d.toLocaleDateString("pt-BR"), feriados, diasExtras: feriados.length };
  } catch { return vazio; }
}

/**
 * Data prevista somando `dias` dias ÚTEIS à data base.
 *
 * ⚠ CORREÇÃO (29/07/2026): a versão anterior avançava um dia útil ANTES de
 * começar a contar, devolvendo base + dias + 1. Emissão 29/07 (quarta) com
 * prazo de 1 dia útil resultava em 31/07 (sexta) em vez de 30/07 (quinta).
 * Isso afrouxava TODOS os prazos em um dia útil e inflava o OTIF.
 *
 * @param {string} dataISO base "AAAA-MM-DD"
 * @param {number} dias    dias úteis de prazo
 * @param {{municipio?:string, uf?:string, filial?:string}} ctx
 *        recorte para feriados municipais/regionais
 */
/**
 * Quantos dias ÚTEIS separam duas datas, na MESMA convenção de addDiasUteis:
 * conta os dias após a base até chegar ao fim, pulando fim de semana e
 * feriados do contexto.
 *
 * É a operação inversa de addDiasUteis — se esta devolve N, então
 * addDiasUteis(base, N) cai em dataFimISO. Essa simetria é o que permite
 * comparar o tempo REAL de uma entrega com o prazo CONTRATADO sem converter
 * unidade: os dois passam a estar em dias úteis, com os mesmos feriados
 * descontados.
 *
 * Devolve null quando falta data ou quando a entrega antecede o embarque.
 */
export function diasUteisEntre(dataIniISO, dataFimISO, ctx) {
  if (!dataIniISO || !dataFimISO) return null;
  const ini = new Date(dataIniISO + "T12:00:00");
  const fim = new Date(dataFimISO + "T12:00:00");
  if (isNaN(ini) || isNaN(fim) || fim < ini) return null;
  let n = 0, guarda = 0;
  const d = new Date(ini);
  // Guarda contra data absurda na base: 400 iterações cobrem mais de um ano.
  while (d < fim && guarda++ < 400) {
    d.setDate(d.getDate() + 1);
    if (isUtil(d, ctx)) n++;
  }
  return n;
}

export function addDiasUteis(dataISO, dias, ctx) {
  if (!dataISO || !dias || isNaN(+dias)) return "—";
  try {
    const d = new Date(dataISO + "T12:00:00");
    if (isNaN(d.getTime())) return "—";
    let c = 0;
    while (c < dias) {
      d.setDate(d.getDate() + 1);
      if (isUtil(d, ctx)) c++;
    }
    return d.toLocaleDateString("pt-BR");
  } catch { return "—"; }
}

/** O dia útil anterior a `dataISO`, em "dd/mm/aaaa" — mesmos feriados de addDiasUteis (#369). */
export function diaUtilAnterior(dataISO, ctx) {
  const d = new Date(dataISO + "T12:00:00");
  if (isNaN(d.getTime())) return "";
  do d.setDate(d.getDate() - 1); while (!isUtil(d, ctx));
  return d.toLocaleDateString("pt-BR");
}

// ─── Trânsito acima do esperado ─────────────────────────────────────
// Data de entrega muito distante do embarque quase nunca é viagem longa: é
// erro de digitação (ano ou mês trocado) ou baixa lançada em bloco muito
// depois. Uma vez gravada, a data contamina OTIF, Relatório Mensal e a
// mediana da rota na Revisão de Prazos — e sai cara de achar depois.
//
// Por isso o número não é recusado, e sim SEGURADO: existem entregas
// legítimas demoradas (reentrega, cliente fechado, sinistro). Quem importa
// confirma e justifica; a justificativa fica na nota.
//
// 10 dias úteis são duas semanas de calendário para rotas cujo prazo
// contratado é de 1 a 5 dias — folgado o bastante para não atrapalhar a
// rotina e apertado o bastante para pegar o que interessa.
export const DIAS_TRANSITO_ALERTA = 10;

const _brISO = br => {
  const p = String(br || "").split("/");
  return p.length === 3 ? `${p[2]}-${p[1]}-${p[0]}` : "";
};

/**
 * Devolve o número de dias úteis quando o trânsito atinge o limiar de
 * alerta, ou null quando está dentro do esperado (ou não é calculável).
 *
 * Mede do embarque, com a emissão como piso — a mesma referência que
 * entregaInvalida usa, para as duas checagens nunca discordarem sobre qual
 * data é o começo da viagem.
 */
export function transitoAcimaEsperado(nota, dataEntregaBR, ctx, limite) {
  const refBR = nota?.dataEmbarque || nota?.dataEmissao;
  if (!refBR || !dataEntregaBR) return null;
  const dias = diasUteisEntre(_brISO(refBR), _brISO(dataEntregaBR), ctx);
  const lim = limite ?? DIAS_TRANSITO_ALERTA;
  return dias != null && dias >= lim ? dias : null;
}

// ─── Regra de transportadora por cliente ────────────────────────────

// ─── Severidade do atraso ───────────────────────────────────────────
// Atraso não é binário. Um dia além da previsão é ruim; cinco dias é outra
// conversa — costuma significar carga parada, extravio ou reentrega, e o
// cliente já ligou. Tratar os dois como "atrasado" esconde exatamente os
// casos que precisam de ação.
//
// Contagem em dias CORRIDOS a partir da previsão (regra confirmada: o prazo
// é em dias úteis, mas o atraso, uma vez estourado, conta corrido).
//
//   1 dia      leve     — ruim, mas dentro do que a operação absorve
//   2 a 5      grave    — destaque: exige explicação da transportadora
//   6 ou mais  crítico  — destaque absoluto: falha de serviço
export const FAIXAS_ATRASO = {
  no_prazo: { cod:"no_prazo", rot:"No prazo",       ordem:0 },
  leve:     { cod:"leve",     rot:"Atraso leve",    ordem:1, min:1, max:1 },
  grave:    { cod:"grave",    rot:"Atraso",          ordem:2, min:2, max:5 },
  critico:  { cod:"critico",  rot:"Atraso crítico", ordem:3, min:6 },
};

/** Dias corridos entre a previsão e a entrega. Negativo = adiantada. */
export function atrasoDias(previsaoISO, entregaBR) {
  if (!previsaoISO || !entregaBR) return null;
  const p = String(previsaoISO).slice(0, 10);
  const partes = String(entregaBR).split("/");
  if (partes.length !== 3) return null;
  const e = `${partes[2]}-${partes[1]}-${partes[0]}`;
  const dp = new Date(p + "T00:00:00"), de = new Date(e + "T00:00:00");
  if (isNaN(dp) || isNaN(de)) return null;
  return Math.round((de - dp) / 86400000);
}

/** Faixa de severidade a partir dos dias de atraso. */
export function faixaAtraso(dias) {
  if (dias == null || dias <= 0) return FAIXAS_ATRASO.no_prazo;
  if (dias === 1) return FAIXAS_ATRASO.leve;
  if (dias <= 5)  return FAIXAS_ATRASO.grave;
  return FAIXAS_ATRASO.critico;
}

// ─── Notas fora dos indicadores ─────────────────────────────────────
// Nota que não chegou a ser transportada não pode entrar em OTIF, prazo ou
// "possível atraso" — não houve entrega para julgar.
//
//   devolvida       — material recusado/devolvido pelo cliente
//   desconsiderada  — nota que não foi para entrega (falta de material,
//                     erro de faturamento, cancelamento interno). Fica na
//                     base para rastreabilidade fiscal, fora dos indicadores.
//
// Centralizado aqui porque a checagem estava repetida como
// `statusNF !== "devolvida"` em mais de vinte pontos: acrescentar um status
// novo sem este helper deixaria metade das telas contando o que não deve.
export const STATUS_FORA_INDICADOR = ["devolvida", "desconsiderada"];

/**
 * Cliente da nota está marcado como funcionário da empresa?
 *
 * Funcionário é pessoa física, recebe em casa e atrasa com frequência —
 * atraso que não é da transportadora. A marca vive no cadastro
 * (`clientes.extras.funcionario`), NÃO no nome: nome muda, e regra que
 * depende de texto de cadastro quebra em silêncio (#107).
 */
export function clienteEhFuncionario(nota, clientesMap) {
  if (!clientesMap || !nota) return false;
  const c = clientesMap.get(nota.codCli + "/" + nota.loja)
         || clientesMap.get("_" + nota.codCli);
  return !!c?.funcionario;
}

/**
 * O nome do cliente sugere funcionário?
 *
 * Só para a VARREDURA que marca em massa — a regra de indicador lê a marca
 * do cadastro, nunca o nome. Aqui é chute assistido, e chute assistido pede
 * confirmação humana antes de gravar.
 *
 * Pega as três formas que existem na base: "(FUNCIONARIO)", "FUNCIONARIA",
 * e os abreviados no fim do nome — "… CARNEIRO FUNC" e "… SOUZA - FU".
 */
export function pareceFuncionario(nome) {
  const n = String(nome || "")
    .normalize("NFD").replace(/[̀-ͯ]/g, "")
    .toUpperCase();
  if (/FUNCIONARI[AO]/.test(n)) return true;
  if (/\bFUNC\b/.test(n)) return true;
  // "- FU" só no fim: no meio do nome seria sobrenome cortado, não abreviação.
  return /-\s*FU\s*$/.test(n);
}

/**
 * Fora dos indicadores de PRAZO: OTIF, atraso e SLA da transportadora.
 *
 * `clientesMap` é opcional de propósito — quem só precisa do status (badge
 * de devolvida, por exemplo) segue chamando com um argumento.
 */
export function foraDoIndicador(nota, clientesMap) {
  return STATUS_FORA_INDICADOR.includes(nota?.statusNF)
      || clienteEhFuncionario(nota, clientesMap);
}

export const TRANSP_POR_FILIAL = {
  "01": "transpCWB",
  "03": "transpMGA",
  "04": "transpCVL",
  "05": "transpRBP",
  "06": "transpMRL",
};

// Prazo por filial de SAÍDA. A distância muda com a origem: uma cidade do
// oeste do Paraná saindo de Cascavel chega em 1 dia e saindo de Curitiba em
// 3. Antes havia um prazo único por município, então a previsão ficava errada
// sempre que a nota saía por uma filial diferente da habitual.
export const PRAZO_POR_FILIAL = {
  "01": "prazoCWB",
  "03": "prazoMGA",
  "04": "prazoCVL",
  "05": "prazoRBP",
  "06": "prazoMRL",
};

/**
 * Prazo do município considerando a filial que emitiu a nota.
 * Devolve null quando não há prazo específico — o chamador decide o fallback.
 */
// ─── Acréscimo de zona rural ────────────────────────────────────────
// Cliente em zona rural leva 2 dias úteis a mais: a transportadora entrega
// na cidade dentro do prazo contratado e a última perna até a propriedade
// não é diária.
//
// ⚠ Aplica-se ao prazo da TABELA DE FRETE e ao prazo por filial. NÃO se
// aplica ao caminho do cadastro de município, que já resolve rural por conta
// própria (municipio.prazoRural, ou prazoPadrao + 2) — somar de novo ali
// contaria os 2 dias duas vezes.
export const DIAS_EXTRA_RURAL = 2;

export function ehRural(cliente) {
  return cliente?.localizacao === "Rural";
}

/** Soma o acréscimo rural a um prazo de rota, quando o cliente for rural. */
export function comAcrescimoRural(prazo, cliente) {
  if (prazo == null) return prazo;
  return ehRural(cliente) ? prazo + DIAS_EXTRA_RURAL : prazo;
}

export function getPrazoFilial(municipio, filialNF) {
  if (!municipio || !filialNF) return null;
  const chave = String(filialNF).trim().padStart(2, "0");
  const campo = PRAZO_POR_FILIAL[chave];
  if (!campo) return null;
  const v = municipio[campo];
  // 0 não é prazo válido (seria entrega no mesmo dia); trata como não informado.
  return v == null || v === "" || +v <= 0 ? null : +v;
}

function normFilial(f) { return String(f||"").replace(/^0+/,""); }

export function transpCorreta(cliente, municipio, filialNF) {
  if (!municipio || !cliente) return null;

  let cod, prazo, via;
  if (cliente.localizacao === "Rural") {
    // ⚠ FALLBACK PARA A TRANSPORTADORA PADRÃO.
    //
    // Zona rural é, antes de tudo, uma regra de PRAZO (+2 dias úteis). Só
    // muda a transportadora quando o município designa uma específica para a
    // área rural — o que é exceção, não regra.
    //
    // Sem este fallback, `cod` vinha vazio e validarNFRapido devolvia
    // "sem_config" para TODO cliente rural: a importação de municípios grava
    // transpRural como "" e nunca preenche. O efeito era perverso — marcar o
    // cliente como rural, que deveria melhorar a medição, tirava a nota da
    // validação e do OTIF.
    cod   = municipio.transpRural || municipio.transpPadrao;
    prazo = municipio.prazoRural ?? ((municipio.prazoPadrao ?? 1) + 2);
    via   = municipio.transpRural ? "Zona Rural" : "Zona Rural (transp. padrão)";
  } else if (cliente.tipo === "MEC" || cliente.tipo === "B2B") {
    // Mesmo raciocínio: sem transportadora MEC designada, vale a padrão.
    cod   = municipio.transpMec || municipio.transpPadrao;
    prazo = municipio.prazoMec ?? municipio.prazoPadrao;
    via   = municipio.transpMec ? "MEC/B2B" : "MEC/B2B (transp. padrão)";
  } else {
    cod   = municipio.transpPadrao;
    prazo = municipio.prazoPadrao;
    via   = "Padrão";
  }

  const filialNorm = normFilial(filialNF);
  // ⚠ FALLBACK PARA municipio.filial.
  //
  // isFilialPadrao decide entre os status "verificar" e "incorreto": só é
  // "incorreto" quando a nota saiu de uma filial DIFERENTE da padrão com a
  // transportadora errada. Com filialPadrao vazio, a expressão abaixo dava
  // sempre true e TODA divergência virava "verificar" — por isso o card
  // "Incorretas" mostrava 0 e o filtro "⚠ Incorreto" não trazia nada, apesar
  // de existirem milhares de notas com transportadora divergente.
  //
  // A importação de municípios preenche `filial` (coluna da planilha) e nunca
  // preencheu `filialPadrao`, que é cadastro manual. Os dois representam a
  // mesma coisa — a filial de faturamento habitual do município.
  const filialPadraoNorm = normFilial(municipio.filialPadrao || municipio.filial);
  // TRANSP_POR_FILIAL usa chaves com zero à esquerda ("01","03"...).
  // normFilial() remove esse zero (usado para comparar filiais entre si),
  // então a consulta ao mapa precisa da versão com padStart — sem isso,
  // a chave nunca batia e a transportadora por filial nunca era aplicada.
  const filialChave  = filialNorm ? filialNorm.padStart(2, "0") : "";
  const campoFilial  = filialChave ? TRANSP_POR_FILIAL[filialChave] : null;
  const codFilial   = campoFilial ? (municipio[campoFilial] || cod) : cod;
  const isFilialPadrao = !filialNorm || !filialPadraoNorm || filialNorm === filialPadraoNorm;

  // Prazo da filial de saída sobrepõe o do município quando cadastrado — a
  // origem da carga determina a distância. Sem cadastro, mantém o anterior.
  //
  // O acréscimo rural entra só sobre o prazo POR FILIAL: o `prazo` calculado
  // acima, no bloco de tipo de cliente, já considera rural (prazoRural ou
  // prazoPadrao + 2). Somar nos dois contaria 4 dias.
  const pFilial = getPrazoFilial(municipio, filialChave);
  const prazoFinal = pFilial != null ? comAcrescimoRural(pFilial, cliente) : prazo;

  return { cod, codFilial, prazo: prazoFinal, via, isFilialPadrao, temTDE: municipio.temTDE };
}

// ─── Validação de NF ────────────────────────────────────────────────

export function criarClientesMap(clientes) {
  const m = new Map();
  (clientes || []).forEach(c => {
    m.set(c.cod + "/" + c.loja, c);
    if (!m.has("_" + c.cod)) m.set("_" + c.cod, c);
  });
  return m;
}

export function criarMunicipiosMap(municipios) {
  const m = new Map();
  (municipios || []).forEach(x => {
    m.set(x.mun + "-" + x.uf, x);
    if (!m.has(x.mun)) m.set(x.mun, x);
  });
  return m;
}

// ─── Validação memoizada ─────────────────────────────────────────
// validarNFRapido é chamada sobre a base INTEIRA várias vezes por render
// (badge da sidebar, alertas, lista de notas, dashboard, análise). Com 70k+
// notas isso vira segundos de CPU repetidos à toa. Este cache guarda o
// resultado por nota e só recalcula quando algo que afeta a validação muda.
// O resultado fica guardado NO PRÓPRIO objeto da nota, com um número de
// versão. Sem Map e sem montar chave de texto — é só uma comparação de
// inteiro. (Uma versão anterior deste cache montava uma chave concatenando
// 6 campos por chamada, o que custava mais que a validação em si.)
// Nota editada vira um objeto novo (spread), então recalcula naturalmente.
let _valVersao = 0;
let _valMaps   = null;

export function validarNFCache(nota, clientesMap, municipiosMap) {
  if (!_valMaps || _valMaps.c !== clientesMap || _valMaps.m !== municipiosMap) {
    _valVersao++;
    _valMaps = { c: clientesMap, m: municipiosMap };
  }
  if (nota._valVer === _valVersao) return nota._valCache;
  const v = validarNFRapido(nota, clientesMap, municipiosMap);
  try { nota._valCache = v; nota._valVer = _valVersao; } catch { /* objeto congelado */ }
  return v;
}

export function validarNFRapido(nota, clientesMap, municipiosMap) {
  const cli = clientesMap
    ? (clientesMap.get(nota.codCli + "/" + nota.loja) || clientesMap.get("_" + nota.codCli))
    : null;
  if (!cli) return { status:"sem_cliente", msg:"Cliente não cadastrado", prazo:null };

  // Fallback para o cadastro ATUAL do cliente: quando a NF foi importada antes
  // do cliente existir, nota.municipio/uf ficaram vazios (congelados). Assim que
  // o cliente é cadastrado, a validação passa a usar o município dele — sem
  // precisar reimportar a nota.
  const munNome = nota.municipioEntrega || nota.municipio || cli.municipio;
  const ufNome  = nota.ufEntrega        || nota.uf        || cli.uf;
  const mun = municipiosMap
    ? (municipiosMap.get(munNome + "-" + ufNome) || municipiosMap.get(munNome))
    : null;
  if (!mun) return { status:"sem_municipio", msg:"Município não cadastrado", prazo:null, cli };

  const filialNF = String(nota.filial||"").replace(/^0+/,"");
  const tc = transpCorreta(cli, mun, filialNF);
  if (!tc?.cod) return { status:"sem_config", msg:"Sem transportadora configurada", prazo: mun.prazoPadrao || null, cli };

  const codUsada = getValAtual(nota, "codTranspUsada");
  const prazo = cli.localizacao === "Rural"
    ? (mun.prazoRural ?? ((mun.prazoPadrao ?? 1) + 2))
    : tc.cod === mun.transpMec ? (mun.prazoMec ?? mun.prazoPadrao)
    : mun.prazoPadrao;

  const codCorretoFilial = tc.codFilial || tc.cod;

  if (codUsada === codCorretoFilial) {
    if (tc.isFilialPadrao) {
      return { status:"ok", via:tc.via, codCorreto:codCorretoFilial, prazo, temTDE:tc.temTDE, cli };
    } else {
      return {
        status:"manual_correta",
        msg:"Transp. correta para filial " + filialNF + " (não é a filial padrão do município)",
        codCorreto: codCorretoFilial, via: tc.via, prazo, temTDE: tc.temTDE, cli,
      };
    }
  }

  const tCorreto = listarTransportadoras().find(t => t.cod === codCorretoFilial);
  const nomeCerto = tCorreto?.nome || codCorretoFilial;

  if (tc.isFilialPadrao) {
    return {
      status:"verificar",
      msg:"Filial correta, mas deveria ser " + codCorretoFilial + " (" + nomeCerto + ")",
      codCorreto: codCorretoFilial, via: tc.via, prazo, temTDE: tc.temTDE, cli,
    };
  }

  return {
    status:"incorreto",
    msg:"Filial " + filialNF + ": deveria ser " + codCorretoFilial + " (" + nomeCerto + ")",
    codCorreto: codCorretoFilial, via: tc.via, prazo, temTDE: tc.temTDE, cli,
  };
}

export function validarNF(nota, clientes, municipios) {
  const cli = clientes.find(c => c.cod === nota.codCli && c.loja === nota.loja);
  if (!cli) return { status:"sem_cliente", msg:"Cliente não cadastrado" };

  const mun = municipios.find(m => m.mun === nota.municipio && m.uf === nota.uf);
  if (!mun) return { status:"sem_municipio", msg:`Município "${nota.municipio}" não cadastrado` };

  const tc = transpCorreta(cli, mun);
  if (!tc?.cod) return { status:"sem_config", msg:`Transportadora não configurada para ${cli.tipo}/${cli.localizacao}` };

  const codUsada  = getValAtual(nota, "codTranspUsada");
  const prazoReal = tc.prazo ?? mun.prazoPadrao;
  const prazoData = prazoReal ? addDiasUteis(nota.dataISO, prazoReal) : "—";

  if (codUsada === tc.cod) {
    return {
      status:"ok", msg:"Transportadora correta",
      codCorreto:tc.cod, prazo:prazoReal, prazoData, via:tc.via,
      tipoCli:cli.tipo, locCli:cli.localizacao, temTDE:tc.temTDE,
    };
  }

  const tCorreto = listarTransportadoras().find(t => t.cod === tc.cod);
  // Sem a lista carregada, mostra so o codigo — nunca "(undefined)".
  const nome = tCorreto?.short ? ` (${tCorreto.short})` : "";
  return {
    status:"incorreto",
    msg:`Deveria ser ${tc.cod}${nome} via ${tc.via}`,
    codCorreto:tc.cod, codUsada, prazo:prazoReal, prazoData, via:tc.via,
    tipoCli:cli.tipo, locCli:cli.localizacao, temTDE:tc.temTDE,
  };
}

export function getValAtual(nota, campo) {
  const hist = nota.overrides?.[campo];
  return hist?.length > 0 ? hist[hist.length - 1].valor : nota[campo];
}

/**
 * Alguém alterou este campo à mão? (#43)
 *
 * Toda entrada de `overrides` nasce da tela, com justificativa e usuário —
 * não existe override automático. Então a presença de um é, por
 * construção, decisão humana explícita.
 *
 * Serve para o mesmo propósito do `temAlteracaoHumana` de
 * historicoAlteracao.js, que NÃO cobre transportadora: aquele lê
 * `extras.historico`, cujos campos são só "embarque" e "entrega". A troca
 * de transportadora vive em `overrides.codTranspUsada`, que é outra trilha.
 */
export function temOverrideManual(nota, campo) {
  return (nota?.overrides?.[campo]?.length ?? 0) > 0;
}

/** A última alteração manual de um campo, ou null — quem trocou e por quê. */
export function ultimoOverride(nota, campo) {
  const hist = nota?.overrides?.[campo];
  return hist?.length > 0 ? hist[hist.length - 1] : null;
}

// ─── Funções de análise de CT-e e simulação ────────────────────────
// Centralizadas aqui (em vez de ficarem nos módulos visuais) para que
// possam ser testadas sem depender de React ou componentes de UI.

/**
 * Seleciona a tabela de frete correta para um CT-e, priorizando:
 * 1. Tabela da transportadora + vigente na data de emissão
 * 2. Tabela com rota por IBGE do destino (imune a variação de grafia)
 * 3. Tabela com rota por nome do destino (fallback)
 * 4. Tabela com rota "TODOS" (cobre qualquer destino)
 */
export function encontrarTabelaParaCTe(todasTabelas, codTransp, municipioDestino, dataISO, municipioDestinoIBGE) {
  if (!todasTabelas?.length) return null;
  const dest     = (municipioDestino || "").toUpperCase().trim();
  const destIBGE = municipioDestinoIBGE || "";

  let tabsTransp = todasTabelas;
  if (codTransp) {
    const codN    = String(codTransp).replace(/^0+/, "");
    const filtradas = todasTabelas.filter(t => {
      const tN = String(t.codTransp || "").replace(/^0+/, "");
      return tN === codN || t.codTransp === codTransp;
    });
    if (filtradas.length > 0) tabsTransp = filtradas;
  }
  if (tabsTransp.length === 0) return null;

  const tabsVigentes = dataISO && dataISO.length >= 10
    ? tabsTransp.filter(t => {
        const ini = (t.vigIni || "0000-01-01").slice(0, 10);
        const fim = (t.vigFim || "9999-12-31").slice(0, 10);
        return dataISO >= ini && dataISO <= fim;
      })
    : tabsTransp;

  const candidatas = tabsVigentes.length > 0 ? tabsVigentes : tabsTransp;
  if (candidatas.length === 1) return candidatas[0];
  if (!dest && !destIBGE) return candidatas[0];

  if (destIBGE) {
    for (const t of candidatas) {
      if (!t.rotas?.length) continue;
      if (t.rotas.find(r => r.destIBGE && r.destIBGE === destIBGE)) return t;
    }
  }

  if (dest) {
    for (const t of candidatas) {
      if (!t.rotas?.length) continue;
      if (t.rotas.find(r => r.dest?.toUpperCase().trim() === dest)) return t;
    }
  }

  for (const t of candidatas) {
    if (!t.rotas?.length) continue;
    if (t.rotas.find(r => r.dest?.toUpperCase().trim() === "TODOS")) return t;
  }

  return candidatas[0];
}

/**
 * Constrói o objeto de tabela de frete a partir dos campos do formulário
 * de simulação do ComparativoMunicipio.
 * Regra crítica: KG usa `rkg`, MIX usa `pgv` — campos distintos no motor.
 */
export function construirTabelaSimulada(novaTab, faixasDefault = []) {
  return {
    tipo:      novaTab.tipo,
    adv:       +novaTab.adv      || 0,
    advMin:    +novaTab.advMin   || 0,
    pgv:       +novaTab.pgv      || 0,
    rkg:       +novaTab.rkg      || 0,
    minimo:    +novaTab.minimo   || 0,
    icms:      +novaTab.icms     || 0,
    gris:      +novaTab.gris     || 0,
    grisMin:   +novaTab.grisMin  || 0,
    ped:       +novaTab.ped      || 0,
    tas:       +novaTab.tas      || 0,
    desp:      +novaTab.desp     || 0,
    rdc:       +novaTab.rdc      || 0,
    rdcTipo:   novaTab.rdcTipo   || "percNota",
    excedente: +novaTab.excedente|| 0,
    faixas:    novaTab.faixas    || faixasDefault,
    frac:      "Por peso real",
  };
}

// ─── Estimativa de perda por transportadora incorreta ───────────────
// Usa o que já está carregado (NFs + cadastro de clientes/municípios/
// tabelas) para simular o custo real de cada NF com a transportadora
// USADA vs a transportadora CORRETA (mesma regra de transpCorreta).
// Não depende de CT-e importado — roda sobre o histórico de NFs.

/** Vigente hoje: vigIni <= hoje <= vigFim; data vazia = aberta. */
export function tabelaVigente(t, hoje = new Date().toISOString().slice(0, 10)) {
  return hoje >= (t.vigIni || "0000-01-01").slice(0, 10)
      && hoje <= (t.vigFim || "9999-12-31").slice(0, 10);
}

/**
 * Tabelas que cotação, simulação e comparativos podem usar: de transportadora
 * ativa (sem cadastro = ativa) e vigente hoje (#445).
 */
export function tabelasParaCotar(tabelas, transportadoras) {
  const norm = c => String(c || "").replace(/^0+/, "");
  const inativas = new Set((transportadoras || []).filter(t => t.ativo === false).map(t => norm(t.cod)));
  return (tabelas || []).filter(t => !inativas.has(norm(t.codTransp)) && tabelaVigente(t));
}

/**
 * Escolhe a tabela "representativa" de uma transportadora para um
 * município: prioriza tabela vigente hoje com rota cadastrada cobrindo
 * o destino; cai para qualquer tabela vigente da transportadora;
 * por último, qualquer tabela cadastrada (mesmo vencida) — melhor uma
 * estimativa aproximada do que nenhuma.
 */
export function tabelaAtualPara(tabelas, codTransp, municipio, { soVigentes = false } = {}) {
  if (!codTransp || !tabelas?.length) return null;
  const codN = String(codTransp).replace(/^0+/, "");
  const candidatas = tabelas.filter(t => String(t.codTransp||"").replace(/^0+/,"") === codN);
  if (candidatas.length === 0) return null;

  const vigentes = candidatas.filter(t => tabelaVigente(t));
  if (soVigentes && vigentes.length === 0) return null;
  const pool = vigentes.length > 0 ? vigentes : candidatas;
  if (pool.length === 1) return pool[0];

  if (municipio) {
    const comRota = pool.find(t => getRotaMatch(t, null, municipio));
    if (comRota) return comRota;
  }
  return pool[0];
}

/**
 * Varre as NFs de um período e identifica as que usaram transportadora
 * diferente da correta, estimando o impacto financeiro (custoUsado -
 * custoCorreto) com base nas tabelas e taxas já cadastradas.
 *
 * @param {Array} notas
 * @param {Map}   clientesMap   - de criarClientesMap(clientes)
 * @param {Map}   municipiosMap - de criarMunicipiosMap(municipios)
 * @param {Array} tabelas
 * @param {{dias?: number}} opts - período retroativo em dias (padrão 90)
 * @returns {Array} itens com diff estimado (positivo = prejuízo, negativo = economia acidental)
 */
export function estimarPerdasTransportadora(notas, clientesMap, municipiosMap, tabelas, opts = {}) {
  const dias = opts.dias ?? 90;
  const limiteISO = new Date(Date.now() - dias * 86400000).toISOString().slice(0, 10);

  const itens = [];
  for (const n of (notas || [])) {
    if (!n.dataISO || n.dataISO < limiteISO) continue;

    const val = validarNFRapido(n, clientesMap, municipiosMap);
    if (val.status !== "incorreto" && val.status !== "verificar") continue;
    if (!val.codCorreto) continue;

    const codUsada = getValAtual(n, "codTranspUsada");
    const codN1 = String(codUsada || "").replace(/^0+/, "");
    const codN2 = String(val.codCorreto || "").replace(/^0+/, "");
    if (!codUsada || codN1 === codN2) continue; // mesma transportadora, só formatação diferente

    const cli = clientesMap.get(n.codCli + "/" + n.loja) || clientesMap.get("_" + n.codCli);
    const munNome = n.municipioEntrega || n.municipio;
    const ufNome  = n.ufEntrega        || n.uf;
    const mun = municipiosMap.get(munNome + "-" + ufNome) || municipiosMap.get(munNome);
    if (!mun) continue;

    const tabUsada   = tabelaAtualPara(tabelas, codUsada, mun);
    const tabCorreta = tabelaAtualPara(tabelas, val.codCorreto, mun);
    if (!tabUsada || !tabCorreta) {
      // Descartada por falta de tabela vigente para uma das duas rotas.
      // Registra o motivo em opts.diag quando o chamador pedir: sem isso o
      // painel simplesmente não mostrava a divergência e parecia "não estar
      // atualizando" depois de cadastrar uma transportadora ou tabela nova.
      if (opts.diag) {
        const falta = !tabUsada ? codUsada : val.codCorreto;
        opts.diag.semTabela = (opts.diag.semTabela || 0) + 1;
        opts.diag.codsSemTabela = opts.diag.codsSemTabela || new Map();
        const k = `${falta}|${mun.mun}/${mun.uf || ""}`;
        opts.diag.codsSemTabela.set(k, (opts.diag.codsSemTabela.get(k) || 0) + 1);
      }
      continue;
    }

    const peso = n.pesoBruto || 0;
    const valorNF = n.vlrBruto || 0;
    if (peso <= 0 && valorNF <= 0) continue;

    const custoUsado   = calcFreteTotal(tabUsada,   peso, valorNF, mun, cli);
    const custoCorreto = calcFreteTotal(tabCorreta, peso, valorNF, mun, cli);
    if (custoUsado <= 0 || custoCorreto <= 0) continue; // alguma tabela zerada — descarta para não distorcer

    itens.push({
      notaId:        n._id || n.id,
      numero:        n.numero,
      filial:        n.filial,
      dataISO:       n.dataISO,
      dataEmissao:   n.dataEmissao,
      municipio:     mun.mun,
      uf:            mun.uf,
      codCli:        n.codCli,
      loja:          n.loja,
      clienteNome:   cli?.nome || "",
      transpUsada:   codUsada,
      transpCorreta: val.codCorreto,
      custoUsado,
      custoCorreto,
      diff:          +(custoUsado - custoCorreto).toFixed(2),
      peso, valorNF,
    });
  }
  return itens;
}

/**
 * Agrupa o resultado de estimarPerdasTransportadora por município,
 * cliente e par "transportadora usada → correta", já ordenado do
 * maior impacto para o menor.
 */
export function agruparPerdas(itens) {
  const porMun = {}, porCli = {}, porPar = {};
  let totalDiff = 0, totalPrejuizo = 0, totalEconomia = 0;

  itens.forEach(it => {
    totalDiff += it.diff;
    if (it.diff > 0) totalPrejuizo += it.diff; else totalEconomia += -it.diff;

    const mk = it.municipio + "/" + it.uf;
    if (!porMun[mk]) porMun[mk] = { municipio: it.municipio, uf: it.uf, total: 0, count: 0, prejuizo: 0 };
    porMun[mk].total += it.diff;
    porMun[mk].count++;
    if (it.diff > 0) porMun[mk].prejuizo += it.diff;

    const ck = it.codCli + "/" + it.loja;
    if (!porCli[ck]) porCli[ck] = { codCli: it.codCli, loja: it.loja, clienteNome: it.clienteNome, total: 0, count: 0 };
    porCli[ck].total += it.diff;
    porCli[ck].count++;

    const pk = it.transpUsada + "→" + it.transpCorreta;
    if (!porPar[pk]) porPar[pk] = { usada: it.transpUsada, correta: it.transpCorreta, total: 0, count: 0 };
    porPar[pk].total += it.diff;
    porPar[pk].count++;
  });

  return {
    porMun: Object.values(porMun).sort((a,b) => b.prejuizo - a.prejuizo),
    porCli: Object.values(porCli).sort((a,b) => b.total - a.total),
    porPar: Object.values(porPar).sort((a,b) => b.total - a.total),
    totalDiff: +totalDiff.toFixed(2),
    totalPrejuizo: +totalPrejuizo.toFixed(2),
    totalEconomia: +totalEconomia.toFixed(2),
    count: itens.length,
  };
}